// Seller "My Listings" — URL search params <-> filter state.
//
// The filters live in the URL so a link (seller home "listings with issues"
// card -> `?status=issues`), back/forward, refresh and a shared link all open
// the same filtered list. Defaults are omitted so the plain URL stays clean.
//
//   ?store=<store_integration_id>
//   ?status=live|not_live|archived   (all = omitted)
//   ?issues=1                        (issues-only toggle)
//   ?q=<text>
//   ?sort=variations|stock|lastEdited (title = omitted)
//
// Legacy/short form: `?status=issues` means "issues only, any status" — the
// seller home card links that way.

export type ListingsStatusFilter = 'all' | 'live' | 'not_live' | 'archived'
export type ListingsSortKey = 'title' | 'variations' | 'stock' | 'lastEdited'

export type SellerListingsSearch = {
  store?: string
  status?: Exclude<ListingsStatusFilter, 'all'>
  issues?: true
  q?: string
  sort?: Exclude<ListingsSortKey, 'title'>
}

export type ListingsFilterState = {
  store: string
  status: ListingsStatusFilter
  issues: boolean
  q: string
  sort: ListingsSortKey
}

export const DEFAULT_LISTINGS_FILTERS: ListingsFilterState = {
  store: '',
  status: 'all',
  issues: false,
  q: '',
  sort: 'title',
}

const STATUSES = new Set<string>(['live', 'not_live', 'archived'])
const SORTS = new Set<string>(['variations', 'stock', 'lastEdited'])

function isTruthyFlag(value: unknown): boolean {
  return value === true || value === 1 || value === '1' || value === 'true'
}

/** Route `validateSearch` for /seller/my-listings — unknown values are dropped. */
export function parseSellerListingsSearch(
  search: Record<string, unknown>,
): SellerListingsSearch {
  const rawStatus = typeof search.status === 'string' ? search.status : undefined
  const status =
    rawStatus && STATUSES.has(rawStatus)
      ? (rawStatus as SellerListingsSearch['status'])
      : undefined
  const issues = rawStatus === 'issues' || isTruthyFlag(search.issues)
  const sort =
    typeof search.sort === 'string' && SORTS.has(search.sort)
      ? (search.sort as SellerListingsSearch['sort'])
      : undefined
  const out: SellerListingsSearch = {}
  if (typeof search.store === 'string' && search.store) out.store = search.store
  if (status) out.status = status
  if (issues) out.issues = true
  if (typeof search.q === 'string' && search.q.trim()) out.q = search.q
  if (sort) out.sort = sort
  return out
}

export function filtersFromSearch(search: Record<string, unknown>): ListingsFilterState {
  const parsed = parseSellerListingsSearch(search)
  return {
    store: parsed.store ?? '',
    status: parsed.status ?? 'all',
    issues: parsed.issues === true,
    q: parsed.q ?? '',
    sort: parsed.sort ?? 'title',
  }
}

export function searchFromFilters(filters: ListingsFilterState): SellerListingsSearch {
  const out: SellerListingsSearch = {}
  if (filters.store) out.store = filters.store
  if (filters.status !== 'all') out.status = filters.status
  if (filters.issues) out.issues = true
  if (filters.q.trim()) out.q = filters.q.trim()
  if (filters.sort !== 'title') out.sort = filters.sort
  return out
}

const OWN_KEYS = ['store', 'status', 'issues', 'q', 'sort'] as const

/**
 * Next URL search after a filter change: applies `patch` on top of the current
 * search and keeps any params this page does not own (e.g. `uiPreview`).
 */
export function applyListingsFilterPatch(
  prev: Record<string, unknown>,
  patch: Partial<ListingsFilterState>,
): Record<string, unknown> {
  const rest: Record<string, unknown> = { ...prev }
  for (const key of OWN_KEYS) delete rest[key]
  return { ...rest, ...searchFromFilters({ ...filtersFromSearch(prev), ...patch }) }
}
